<template>
    <div class="field-creator">
        <a-form @submit.prevent="mode === 'create' ? createField() : updateField()">
            <!-- Ortak Inputlar -->
            <div style="display: flex; gap: 16px">
                <a-form-item :label="$t('İkon')" style="flex: 0 0 auto">
                    <MaterialIconPicker v-model="cloneField.icon" />
                </a-form-item>
                <a-form-item :label="$t('message.name')" :required="true" style="flex: 1">
                    <a-input v-model="cloneField.name" />
                </a-form-item>
            </div>

            <a-form-item :label="$t('tables.fieldType')" :required="true">
                <a-select v-model="cloneField.fieldType" @change="onFieldTypeChange" :disabled="mode === 'update'">
                    <a-select-option v-for="type in fieldTypes" :key="type.value" :value="type.value">
                        {{ type.label }}
                    </a-select-option>
                </a-select>
            </a-form-item>

            <a-form-item :label="$t('tables.alignment')" :required="true">
                <a-select v-model="cloneField.alignment">
                    <a-select-option value="left">{{ $t("tables.left") }}</a-select-option>
                    <a-select-option value="center">{{ $t("tables.center") }}</a-select-option>
                    <a-select-option value="right">{{ $t("tables.right") }}</a-select-option>
                </a-select>
            </a-form-item>

            <!-- Varsayılan Değer (Default Value) -->
            <a-form-item
                :label="$t('Varsayılan Değer')"
                v-if="
                    !cloneField.fieldType.startsWith('record_') &&
                    !['formula', 'lookup', 'auto-increment'].includes(cloneField.fieldType)
                "
            >
                <!-- Default value for text, email, url, color, phone, long-text -->
                <a-input
                    v-if="['text', 'email', 'url', 'color', 'phone', 'long-text'].includes(cloneField.fieldType)"
                    v-model="cloneField.defaultValue"
                    :placeholder="$t('Varsayılan bir değer girin')"
                />

                <!-- Default value for number, currency -->
                <a-input-number
                    v-else-if="['number', 'currency'].includes(cloneField.fieldType)"
                    v-model="cloneField.defaultValue"
                    style="width: 100%"
                />

                <!-- Default value for datetime -->
                <div
                    v-else-if="cloneField.fieldType === 'datetime'"
                    style="display: flex; gap: 10px; align-items: center"
                >
                    <a-date-picker
                        v-if="!useCurrentDateTimeAsDefault"
                        v-model="cloneField.defaultValue"
                        :showTime="cloneField.options.datetime.includeTime"
                        :format="
                            cloneField.options.datetime.includeTime
                                ? cloneField.options.datetime.format + ' ' + cloneField.options.datetime.timeFormat
                                : cloneField.options.datetime.format
                        "
                        style="width: 100%"
                    />
                    <span v-else>{{ $t("Şu anki tarih/saat") }}</span>
                    <a-checkbox v-model="useCurrentDateTimeAsDefault">{{
                        $t("Şu anki tarih/saati kullan")
                    }}</a-checkbox>
                </div>

                <!-- Default value for checkbox -->
                <a-switch v-else-if="cloneField.fieldType === 'checkbox'" v-model="cloneField.defaultValue" />

                <!-- Default value for dropdown -->
                <div v-else-if="cloneField.fieldType === 'dropdown'">
                    <a-select
                        v-if="cloneField.options.dropdown.mode === 'static'"
                        v-model="cloneField.defaultValue"
                        :mode="cloneField.options.dropdown.multiple ? 'multiple' : undefined"
                        style="width: 100%"
                    >
                        <a-select-option
                            v-for="(option, index) in cloneField.options.dropdown.options"
                            :key="index"
                            :value="option.value"
                        >
                            {{ option.value }}
                        </a-select-option>
                    </a-select>
                    <a-input v-else disabled :placeholder="$t('Dinamik dropdown için varsayılan değer belirlenemez')" />
                </div>

                <!-- Default value for json -->
                <a-textarea
                    v-else-if="cloneField.fieldType === 'json'"
                    v-model="cloneField.defaultValue"
                    rows="4"
                    :placeholder="$t('Geçerli bir JSON değeri girin')"
                />

                <!-- Default value for linked records -->
                <a-input
                    v-else-if="
                        ['linked-record', 'contact', 'ticket', 'task', 'event', 'conversation', 'user'].includes(
                            cloneField.fieldType
                        )
                    "
                    disabled
                    :placeholder="$t('Bu alan tipine varsayılan değer belirlenemez')"
                />

                <!-- Default value for file/media fields -->
                <a-input
                    v-else-if="['file', 'image', 'video', 'audio'].includes(cloneField.fieldType)"
                    disabled
                    :placeholder="$t('Dosya alanları için varsayılan değer ayarlanamaz')"
                />

                <!-- Default value for AI -->
                <a-input
                    v-else-if="cloneField.fieldType === 'ai'"
                    v-model="cloneField.defaultValue"
                    :placeholder="$t('AI tarafından üretilecek ilk değer (opsiyonel)')"
                />

                <!-- Default fallback for other types -->
                <a-input v-else v-model="cloneField.defaultValue" />
            </a-form-item>

            <a-form-item :label="$t('tables.fieldType')" :required="true">
                <a-select v-model="cloneField.fieldType" @change="onFieldTypeChange" :disabled="mode === 'update'">
                    <a-select-option v-for="type in fieldTypes" :key="type.value" :value="type.value">
                        {{ type.label }}
                    </a-select-option>
                </a-select>
            </a-form-item>

            <a-form-item :label="$t('tables.alignment')" :required="true">
                <a-select v-model="cloneField.alignment">
                    <a-select-option value="left">{{ $t("tables.left") }}</a-select-option>
                    <a-select-option value="center">{{ $t("tables.center") }}</a-select-option>
                    <a-select-option value="right">{{ $t("tables.right") }}</a-select-option>
                </a-select>
            </a-form-item>

            <!-- Özel Inputlar -->
            <template v-if="cloneField.fieldType === 'datetime'">
                <a-divider>{{ $t("tables.datetimeOptions") }}</a-divider>
                <a-form-item :label="$t('tables.timezone')">
                    <a-auto-complete
                        v-model="cloneField.options.datetime.timezone"
                        :dataSource="timezones"
                        :placeholder="$t('tables.timezone')"
                        :filterOption="filterOption"
                    />
                </a-form-item>
                <a-form-item label="Format">
                    <a-select v-model="cloneField.options.datetime.format">
                        <a-select-option value="MM/DD/YYYY">MM/DD/YYYY (12/31/2023)</a-select-option>
                        <a-select-option value="DD/MM/YYYY">DD/MM/YYYY (31/12/2023)</a-select-option>
                        <a-select-option value="YYYY-MM-DD">YYYY-MM-DD (2023-12-31)</a-select-option>
                        <a-select-option value="MM-DD-YYYY">MM-DD-YYYY (12-31-2023)</a-select-option>
                        <a-select-option value="DD-MM-YYYY">DD-MM-YYYY (31-12-2023)</a-select-option>
                        <a-select-option value="YYYY/MM/DD">YYYY/MM/DD (2023/12/31)</a-select-option>
                        <a-select-option value="MMM DD, YYYY">MMM DD, YYYY (Dec 31, 2023)</a-select-option>
                        <a-select-option value="DD MMM, YYYY">DD MMM, YYYY (31 Dec, 2023)</a-select-option>
                        <a-select-option value="MMMM DD, YYYY">MMMM DD, YYYY (December 31, 2023)</a-select-option>
                        <a-select-option value="DD MMMM, YYYY">DD MMMM, YYYY (31 December, 2023)</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('tables.includeTime')">
                    <a-switch v-model="cloneField.options.datetime.includeTime" />
                </a-form-item>
                <a-form-item v-if="cloneField.options.datetime.includeTime" :label="$t('tables.timeFormat')">
                    <a-select v-model="cloneField.options.datetime.timeFormat">
                        <a-select-option value="HH:mm">HH:mm (23:59)</a-select-option>
                        <a-select-option value="hh:mm A">hh:mm A (11:59 PM)</a-select-option>
                        <a-select-option value="HH:mm:ss">HH:mm:ss (23:59:59)</a-select-option>
                        <a-select-option value="hh:mm:ss A">hh:mm:ss A (11:59:59 PM)</a-select-option>
                        <a-select-option value="HH:mm:ss.SSS">HH:mm:ss.SSS (23:59:59.999)</a-select-option>
                        <a-select-option value="hh:mm:ss.SSS A">hh:mm:ss.SSS A (11:59:59.999 PM)</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('Varsayılan Zaman Olarak Şu An Aktif Edilsin')">
                    <a-switch v-model="cloneField.options.datetime.defaultNow" />
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'number'">
                <a-divider>{{ $t("tables.numberOptions") }}</a-divider>
                <a-form-item :label="$t('tables.decimalFormat')">
                    <a-select v-model="cloneField.options.number.decimalFormat">
                        <a-select-option value="0">0</a-select-option>
                        <a-select-option value="0.0">0.0</a-select-option>
                        <a-select-option value="0.00">0.00</a-select-option>
                        <a-select-option value="0.000">0.000</a-select-option>
                        <a-select-option value="0.0000">0.0000</a-select-option>
                        <a-select-option value="0.00000">0.00000</a-select-option>
                        <a-select-option value="0.000000">0.000000</a-select-option>
                    </a-select>
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'currency'">
                <a-divider>{{ $t("tables.currencyOptions") }}</a-divider>
                <a-form-item :label="$t('tables.currencySymbol')">
                    <a-select v-model="cloneField.options.currency.symbol">
                        <a-select-option value="$">$ (USD)</a-select-option>
                        <a-select-option value="€">€ (EUR)</a-select-option>
                        <a-select-option value="£">£ (GBP)</a-select-option>
                        <a-select-option value="¥">¥ (JPY)</a-select-option>
                        <a-select-option value="₽">₽ (RUB)</a-select-option>
                        <a-select-option value="₹">₹ (INR)</a-select-option>
                        <a-select-option value="₺">₺ (TRY)</a-select-option>
                        <a-select-option value="₩">₩ (KRW)</a-select-option>
                        <a-select-option value="R$">R$ (BRL)</a-select-option>
                        <a-select-option value="¥">¥ (CNY)</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('tables.decimalFormat')">
                    <a-select v-model="cloneField.options.currency.decimalFormat">
                        <a-select-option value="0">0</a-select-option>
                        <a-select-option value="0.0">0.0</a-select-option>
                        <a-select-option value="0.00">0.00</a-select-option>
                        <a-select-option value="0.000">0.000</a-select-option>
                        <a-select-option value="0.0000">0.0000</a-select-option>
                        <a-select-option value="0.00000">0.00000</a-select-option>
                        <a-select-option value="0.000000">0.000000</a-select-option>
                    </a-select>
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'dropdown'">
                <a-divider>{{ $t("tables.dropdownOptions") }}</a-divider>
                <a-form-item :label="$t('tables.mode')">
                    <a-select v-model="cloneField.options.dropdown.mode">
                        <a-select-option value="static">{{ $t("tables.statics") }}</a-select-option>
                        <a-select-option value="dynamic">Dynamic</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('tables.multipleSelection')">
                    <a-switch v-model="cloneField.options.dropdown.multiple" :disabled="mode === 'update'" />
                </a-form-item>

                <!-- Static Mode Options -->
                <div v-if="cloneField.options.dropdown.mode === 'static'">
                    <a-form-item :label="$t('message.options')">
                        <div
                            v-for="(option, index) in cloneField.options.dropdown.options"
                            :key="index"
                            class="option-item"
                        >
                            <a-input v-model="option.value" :placeholder="$t('message.value')" style="width: 60%" />
                            <a-select
                                v-model="option.color"
                                :placeholder="$t('tables.color')"
                                style="width: 30%; margin-left: 10px"
                            >
                                <template #suffixIcon>
                                    <div
                                        class="color-box"
                                        :style="{ backgroundColor: selectedColor(option.color) }"
                                    ></div>
                                </template>
                                <a-select-option
                                    v-for="(color, colorIndex) in colors"
                                    :key="colorIndex"
                                    :value="color.value"
                                >
                                    <div class="color-option">
                                        <div class="color-box" :style="{ backgroundColor: color.value }"></div>
                                        <span>{{ color.label }}</span>
                                    </div>
                                </a-select-option>
                            </a-select>
                            <a-button
                                type="danger"
                                icon="delete"
                                @click="removeOption(index)"
                                style="margin-left: 10px"
                            />
                        </div>
                        <a-button type="dashed" icon="plus" @click="addOption">{{ $t("tables.addOption") }}</a-button>
                    </a-form-item>
                </div>

                <!-- Dynamic Mode Options -->
                <div v-else-if="cloneField.options.dropdown.mode === 'dynamic'">
                    <a-form-item label="Tablo ve Kolon Seçimi">
                        <div style="display: flex; gap: 10px">
                            <a-select
                                v-model="cloneField.options.dropdown.remoteTable"
                                @change="onTableChange"
                                placeholder="Tablo Seç"
                                style="flex: 1"
                            >
                                <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                                    {{ table.name }}
                                </a-select-option>
                            </a-select>
                            <a-select
                                v-model="cloneField.options.dropdown.remoteTableField"
                                :disabled="!cloneField.options.dropdown.remoteTable"
                                placeholder="Kolon Seç"
                                style="flex: 1"
                            >
                                <a-select-option v-for="field in getStringFields" :key="field._id" :value="field._id">
                                    {{ field.name }}
                                </a-select-option>
                            </a-select>
                        </div>
                    </a-form-item>
                </div>
            </template>

            <template v-else-if="cloneField.fieldType === 'automation-button'">
                <a-divider>{{ $t("tables.automationButtonOptions") }}</a-divider>
                <a-form-item :label="$t('automation.automationName')">
                    <a-input v-model="cloneField.options.button.automationName" />
                </a-form-item>
                <a-form-item :label="$t('tables.defaultLabel')" :required="true">
                    <a-input v-model="cloneField.options.button.defaultLabel" />
                </a-form-item>
                <a-form-item :label="$t('tables.disableWhenClicked')">
                    <a-switch v-model="cloneField.options.button.disableWhenClicked" />
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'linked-record'">
                <a-divider>Linked Record Options</a-divider>
                <a-form-item label="Select Table">
                    <a-select
                        v-model="cloneField.options.linkedRecord.table"
                        @change="onLinkedTableChange"
                        placeholder="Select Table"
                    >
                        <a-select-option v-for="table in tables" :key="table._id" :value="table._id">
                            {{ table.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item label="Label Field" v-if="cloneField.options.linkedRecord.table">
                    <a-select v-model="cloneField.options.linkedRecord.labelField" placeholder="Select Label Field">
                        <a-select-option v-for="field in getLinkedTableFields" :key="field._id" :value="field._id">
                            {{ field.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'ai'">
                <a-divider>{{ $t("Yapay Zeka Ayarları") }}</a-divider>
                <a-form-item label="Prompt" :required="true">
                    <a-textarea
                        v-model="cloneField.options.ai.prompt"
                        :rows="4"
                        :placeholder="
                            $t(
                                'Promptu buraya giriniz. Veri analizi yapmasını istebilirsiniz. Kayıtı kategorize etmek için kullanabilirsiniz. Duygu analizi veya çeviri yapmasını isteyebilirsiniz.'
                            )
                        "
                    />
                </a-form-item>
                <a-form-item :label="$t('Ne zaman çalıştırılsın')" :required="true">
                    <a-select v-model="cloneField.options.ai.runAt">
                        <a-select-option value="only-on-create">
                            {{ $t("Kayıt Oluşturulduğunda Çalışır") }}
                        </a-select-option>
                        <a-select-option value="on-every-change">
                            {{ $t("Her Kayıt Değiştiğinde Çalışır") }}
                        </a-select-option>
                        <a-select-option value="manual">
                            {{ $t("Button ile tetiklediğinde") }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item :label="$t('İşlenecek Alanlar')">
                    <a-select v-model="cloneField.options.ai.whichFieldsToProcess" @change="onFieldsProcessingChange">
                        <a-select-option value="all">{{ $t("Tüm Alanlar") }}</a-select-option>
                        <a-select-option value="custom">{{ $t("Özel Alanlar") }}</a-select-option>
                    </a-select>
                </a-form-item>
                <a-form-item v-if="cloneField.options.ai.whichFieldsToProcess === 'custom'" :label="$t('Alan Seçimi')">
                    <a-select
                        v-model="cloneField.options.ai.fieldsToProcess"
                        mode="multiple"
                        :placeholder="$t('İşlenecek alanları seçin')"
                    >
                        <a-select-option v-for="field in tableFields" :key="field._id" :value="field._id">
                            {{ field.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'formula'">
                <a-divider>{{ $t("Formül Ayarları") }}</a-divider>
                <a-form-item label="Formül İfadesi" :required="true">
                    <FormulaFieldEditor
                        v-model="cloneField.options.formula.expression"
                        :tableId="tableId"
                        :allFields="tableFields"
                    />
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'lookup'">
                <a-divider>{{ $t("Lookup Ayarları") }}</a-divider>
                <a-form-item label="Bağlantılı Kayıt Alanı" :required="true">
                    <a-select
                        v-model="cloneField.options.lookup.linkedRecordField"
                        @change="onLinkedRecordFieldChange"
                        placeholder="Bağlantılı Kayıt Alanını Seçin"
                    >
                        <a-select-option v-for="field in getLinkedRecordFields" :key="field._id" :value="field._id">
                            {{ field.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>

                <a-form-item label="Hedef Alan" :required="true" v-if="cloneField.options.lookup.linkedRecordField">
                    <a-select v-model="cloneField.options.lookup.targetField" placeholder="Hedef Alanı Seçin">
                        <a-select-option v-for="field in getTargetFields" :key="field._id" :value="field._id">
                            {{ field.name }}
                        </a-select-option>
                    </a-select>
                </a-form-item>
            </template>

            <template v-else-if="cloneField.fieldType === 'auto-increment'">
                <a-divider>{{ $t("Auto Increment Ayarları") }}</a-divider>

                <template v-if="mode === 'create'">
                    <a-form-item :label="$t('Format')" :required="true">
                        <a-select v-model="cloneField.options.autoIncrement.format" @change="onFieldTypeChange">
                            <a-select-option value="number">{{ $t("Sayı") }}</a-select-option>
                            <a-select-option value="string">{{ $t("Metin") }}</a-select-option>
                        </a-select>
                    </a-form-item>
                </template>

                <template v-if="cloneField.options.autoIncrement.format === 'string'">
                    <a-form-item :label="$t('Ön Ek')" :required="false">
                        <a-input v-model="cloneField.options.autoIncrement.prefix" placeholder="Örn: INV-" />
                    </a-form-item>

                    <a-form-item :label="$t('Son Ek')" :required="false">
                        <a-input v-model="cloneField.options.autoIncrement.suffix" placeholder="Örn: -2024" />
                    </a-form-item>
                </template>

                <a-form-item :label="$t('Başlangıç Değeri')" :required="true">
                    <a-input-number
                        v-model="cloneField.options.autoIncrement.startValue"
                        :min="1"
                        :step="1"
                        :disabled="mode === 'update'"
                    />
                </a-form-item>

                <a-form-item :label="$t('Artış Miktarı')" :required="true">
                    <a-input-number v-model="cloneField.options.autoIncrement.increment" :min="1" :step="1" />
                </a-form-item>
            </template>

            <!-- Form Butonları -->
            <a-form-item>
                <a-button type="primary" html-type="submit" :loading="loading" :disabled="loading">
                    {{ mode === "create" ? $t("tables.create") : $t("message.update") }}
                </a-button>
                <a-button @click="cancel" :disabled="loading">{{ $t("wp.cancel") }}</a-button>
            </a-form-item>
        </a-form>
    </div>
</template>

<script>
import Vue from "vue";
import moment from "moment-timezone";
import { TABLE_API } from "../../../../../api/table";
import { mapState, mapActions } from "vuex";
import MaterialIconPicker from "../../../Module/MaterialIconPicker.vue";
import FormulaFieldEditor from "./FormulaField.vue";

export default Vue.extend({
    name: "FieldCreator",
    components: {
        MaterialIconPicker,
        FormulaFieldEditor,
    },
    props: {
        defaultFieldType: {
            type: String,
            default: null,
        },
        tableId: {
            type: String,
            required: true,
        },
        mode: {
            type: String,
            default: "create",
            validator: (value) => ["create", "update"].includes(value),
        },
        field: {
            type: Object,
            default: null,
        },
    },
    data() {
        return {
            loading: false, // Loading durumunu yönetecek değişken
            cloneField:
                this.mode === "update"
                    ? { ...this.field }
                    : {
                          name: "",
                          fieldType: this.defaultFieldType || "text",
                          alignment: "left",
                          options: {
                              datetime: {
                                  timezone: "UTC",
                                  format: "YYYY-MM-DD",
                                  includeTime: true,
                                  timeFormat: "HH:mm",
                                  defaultNow: true,
                              },
                              number: {
                                  decimalFormat: "0", // Varsayılan değer
                              },
                              currency: {
                                  symbol: "$",
                                  decimalFormat: "00",
                              },
                              autoIncrement: {
                                  prefix: "",
                                  suffix: "",
                                  startValue: 1,
                                  increment: 1,
                                  format: "number",
                                  currentValue: 0,
                              },
                              dropdown: {
                                  mode: "static",
                                  multiple: false,
                                  options: [{ value: "", color: "rgb(210, 216, 243)" }],
                                  remoteTable: null,
                                  remoteTableField: null,
                              },
                              button: {
                                  automationName: "",
                                  defaultLabel: "",
                                  disableWhenClicked: false,
                              },
                              linkedRecord: {
                                  table: null,
                                  labelField: null,
                              },
                              ai: {
                                  prompt: "",
                                  runAt: "only-on-create",
                                  whichFieldsToProcess: "all",
                                  fieldsToProcess: [],
                              },
                              formula: {
                                  expression: "",
                              },
                              lookup: {
                                  linkedRecordField: null,
                                  targetField: null,
                              },
                          },
                      },
            fieldTypes: [
                { value: "text", label: this.$t("tables.text") },
                { value: "long-text", label: this.$t("tables.longText") },
                { value: "number", label: this.$t("tables.number") },
                { value: "currency", label: this.$t("tables.currency") },
                { value: "datetime", label: this.$t("tables.datetime") },
                { value: "dropdown", label: this.$t("tables.dropdown") },
                { value: "checkbox", label: this.$t("tables.checkbox") },
                { value: "url", label: "URL" },
                { value: "email", label: this.$t("tables.email") },
                { value: "phone", label: this.$t("tables.phone") },
                { value: "json", label: "JSON" },
                { value: "file", label: this.$t("tables.file") },
                { value: "image", label: this.$t("tables.image") },
                { value: "video", label: this.$t("tables.video") },
                { value: "audio", label: this.$t("tables.audio") },
                { value: "color", label: this.$t("tables.color") },
                { value: "automation-button", label: this.$t("tables.automationButton") },
                { value: "linked-record", label: "Linked Record" },
                { value: "conversation", label: "Conversation" },
                { value: "ai", label: "AI Field" },
                { value: "record_id", label: "Record ID" },
                { value: "record_created_at", label: "Record Created At" },
                { value: "record_updated_at", label: "Record Updated At" },
                { value: "record_created_by", label: "Record Created By" },
                { value: "record_updated_by", label: "Record Updated By" },
                { value: "formula", label: "Formula" },
                { value: "lookup", label: "Lookup" },
                { value: "auto-increment", label: "Auto Increment" },
            ],
            colors: [
                { label: "Gray", value: "rgb(232, 231, 228)" },
                { label: "Blue", value: "rgb(210, 216, 243)" },
                { label: "Green", value: "rgb(222, 243, 233)" },
                { label: "Yellow", value: "rgb(255, 244, 188)" },
                { label: "Red", value: "rgb(250, 223, 217)" },
                { label: "Pink", value: "rgb(250, 217, 239)" },
                { label: "Purple", value: "rgb(235, 217, 250)" },
                { label: "Cream", value: "rgb(255, 243, 230)" },
            ],
            timezones: moment.tz.names(),
            useCurrentDateTimeAsDefault: false,
        };
    },
    computed: {
        ...mapState("table", ["tables"]),
        getStringFields() {
            if (!this.cloneField.options.dropdown.remoteTable) return [];

            const selectedTable = this.tables.find(
                (table) => table._id === this.cloneField.options.dropdown.remoteTable
            );

            return selectedTable
                ? selectedTable.fields.filter(
                      (field) =>
                          [
                              "ai",
                              "text",
                              "long-text",
                              "autoIncrement",
                              "formula",
                              "url",
                              "email",
                              "phone",
                              "record_id",
                          ].includes(field.fieldType) ||
                          (field?.options?.dropdown?.multiple === false && field.fieldType === "dropdown")
                  )
                : [];
        },
        getLinkedTableFields() {
            if (!this.cloneField.options.linkedRecord.table) return [];

            const selectedTable = this.tables.find((table) => table._id === this.cloneField.options.linkedRecord.table);

            const disAllowedFields = ["automation-button", "linked-record", "checkbox"];
            return selectedTable
                ? selectedTable.fields.filter((field) => !disAllowedFields.includes(field.fieldType))
                : [];
        },
        tableFields() {
            // Mevcut tablonun alanlarını getir
            const currentTable = this.tables.find((table) => table._id === this.tableId);
            return currentTable ? currentTable.fields : [];
        },
        getLinkedRecordFields() {
            // Mevcut tablodaki tüm linked-record tipindeki alanları getir
            return this.tableFields.filter((field) => field.fieldType === "linked-record");
        },
        getTargetFields() {
            if (!this.cloneField.options.lookup.linkedRecordField) return [];

            // Seçilen linked-record field'ını bul
            const linkedRecordField = this.tableFields.find(
                (field) => field._id === this.cloneField.options.lookup.linkedRecordField
            );

            if (!linkedRecordField || !linkedRecordField.options || !linkedRecordField.options.linkedRecord) return [];

            // Bağlı tablonun ID'sini al
            const linkedTableId = linkedRecordField.options.linkedRecord.table;

            // Bağlı tabloyu bul
            const linkedTable = this.tables.find((table) => table._id === linkedTableId);

            // Bağlı tablodaki alanları döndür (özel alan tipleri hariç)
            const disallowedFieldTypes = ["automation-button"];
            return linkedTable
                ? linkedTable.fields.filter((field) => !disallowedFieldTypes.includes(field.fieldType))
                : [];
        },
    },
    watch: {
        useCurrentDateTimeAsDefault(newVal) {
            if (newVal) {
                // Şu anki tarihi kullanıyoruz, ama defaultValue'ya null atıyoruz
                // Backendde bunu "şu anki tarih" olarak işleyecek
                this.cloneField.defaultValue = null;
                this.cloneField.options.datetime.defaultNow = true;
            } else {
                // defaultNow'ı kapatıyoruz eğer açıksa
                this.cloneField.options.datetime.defaultNow = false;
            }
        },
        "cloneField.options.datetime.defaultNow"(newVal) {
            // Sync our checkbox with the defaultNow option
            this.useCurrentDateTimeAsDefault = newVal;
        },
    },
    created() {
        // Eğer tablolar henüz yüklenmemişse yükle
        if (!this.tables.length) {
            this.$store.dispatch("table/fetchCustomerTables");
        }

        // Güncelleme modunda AI alanı için eksik özellikleri ekle
        if (this.mode === "update" && this.cloneField.fieldType === "ai") {
            if (!this.cloneField.options.ai.whichFieldsToProcess) {
                this.$set(this.cloneField.options.ai, "whichFieldsToProcess", "all");
            }
            if (!this.cloneField.options.ai.fieldsToProcess) {
                this.$set(this.cloneField.options.ai, "fieldsToProcess", []);
            }
        }

        // Oluşturma modunda, alan bir metaveri alanıysa (record_ ile başlıyorsa) valueType'ını ayarla
        if (this.mode === "create" && this.cloneField.fieldType.startsWith("record_")) {
            this.setMetadataFieldValueType(this.cloneField.fieldType);
        }

        if (this.mode === "create" && this.cloneField.fieldType === "record_id") {
            this.cloneField.name = "Record ID";
        }
        if (this.mode === "create" && this.cloneField.fieldType === "record_created_at") {
            this.cloneField.name = "Created At";
        }
        if (this.mode === "create" && this.cloneField.fieldType === "record_updated_at") {
            this.cloneField.name = "Updated At";
        }
        if (this.mode === "create" && this.cloneField.fieldType === "record_created_by") {
            this.cloneField.name = "Created By";
        }
        if (this.mode === "create" && this.cloneField.fieldType === "record_updated_by") {
            this.cloneField.name = "Updated By";
        }

        // Eğer datetime alanı ise ve defaultNow seçeneği açıksa, checkbox'ı etkinleştir
        if (
            this.mode === "update" &&
            this.cloneField.fieldType === "datetime" &&
            this.cloneField.options.datetime &&
            this.cloneField.options.datetime.defaultNow
        ) {
            this.useCurrentDateTimeAsDefault = true;
        }
    },
    methods: {
        ...mapActions({
            syncTables: "table/syncTables",
        }),
        setMetadataFieldValueType(fieldType) {
            let valueType = "string";

            if (fieldType === "record_id") {
                valueType = "string";
            } else if (["record_created_at", "record_updated_at"].includes(fieldType)) {
                valueType = "date";
                // Düzenlenemez datetime ayarlarını ekle
                this.cloneField.options.datetime = {
                    timezone: "UTC",
                    format: "YYYY-MM-DD",
                    includeTime: true,
                    timeFormat: "HH:mm",
                    defaultNow: false,
                };
            } else if (["record_created_by", "record_updated_by"].includes(fieldType)) {
                valueType = "objectId";
            }

            this.cloneField.valueType = valueType;

            // Bu alan düzenlenemez olduğunu belirtmek için bir özellik ekleyebiliriz
            this.cloneField.isMetadataField = true;
        },
        onFieldTypeChange() {
            // Metaveri alanı seçildiyse, değer türünü otomatik olarak ayarla
            if (this.cloneField.fieldType.startsWith("record_")) {
                this.setMetadataFieldValueType(this.cloneField.fieldType);
            } else if (this.cloneField.fieldType === "auto-increment") {
                // Auto-increment field için format'a göre valueType'ı ayarla
                const format = this.cloneField.options.autoIncrement.format;
                this.cloneField.valueType = format === "string" ? "string" : "number";
            }

            // Alan tipine göre varsayılan değerleri sıfırlayalım
            this.cloneField.defaultValue = null;
            this.useCurrentDateTimeAsDefault = false;

            // Alan tipine göre varsayılan değeri ayarla
            if (
                this.cloneField.fieldType === "text" ||
                this.cloneField.fieldType === "long-text" ||
                this.cloneField.fieldType === "email" ||
                this.cloneField.fieldType === "url" ||
                this.cloneField.fieldType === "phone" ||
                this.cloneField.fieldType === "color"
            ) {
                this.cloneField.defaultValue = "";
            } else if (this.cloneField.fieldType === "checkbox") {
                this.cloneField.defaultValue = false;
            } else if (this.cloneField.fieldType === "dropdown" && this.cloneField.options.dropdown.multiple) {
                this.cloneField.defaultValue = [];
            } else if (this.cloneField.fieldType === "datetime") {
                // Datetime için defaultNow seçeneğini varsayılan olarak kapatalım
                this.cloneField.options.datetime.defaultNow = false;
            }
        },
        addOption() {
            const nextColor = this.colors[this.cloneField.options.dropdown.options.length % this.colors.length];
            this.cloneField.options.dropdown.options.push({ value: "", color: nextColor.value });
        },
        removeOption(index) {
            this.cloneField.options.dropdown.options.splice(index, 1);
        },

        async createField() {
            this.loading = true; // İşlem başlıyor
            try {
                // Metaveri alanları için değer türünü ayarla
                if (this.cloneField.fieldType.startsWith("record_")) {
                    this.setMetadataFieldValueType(this.cloneField.fieldType);
                }

                // API'ye istek gönder
                const response = await TABLE_API.createField(this.tableId, this.cloneField);
                // Başarılı mesajını göster
                this.$message.success(this.$t("tables.fieldSucsCreate"));
                // Üst componente tabloyu yenilemesini bildir
                this.$emit("field-created-success");
                // Modal'ı kapat
                this.cancel();
                this.syncTables();
            } catch (error) {
                console.error("Alan oluşturma hatası", error);
                // Hata mesajını göster
                this.$message.error(this.$t("tables.fieldCreatingFailed"));
            }
            this.loading = false; // İşlem tamamlandı
        },
        async updateField() {
            this.loading = true;
            try {
                const updatingFields = {
                    name: this.cloneField.name,
                    alignment: this.cloneField.alignment,
                    icon: this.cloneField.icon,
                    position: this.cloneField.position,
                    defaultValue: this.cloneField.defaultValue,
                    options: this.cloneField.options,
                };
                await TABLE_API.updateField(this.tableId, this.cloneField._id, updatingFields);
                this.$message.success(this.$t("tables.fieldSucsUpdate"));
                this.$emit("field-updated-success", this.cloneField);
                this.syncTables();
            } catch (error) {
                console.error("Alan güncelleme hatası", error);
                this.$message.error(this.$t("tables.fieldUpdatingFailed"));
            }
            this.loading = false;
        },
        cancel() {
            // İşlemi iptal et
            this.$emit("cancel");
        },
        selectedColor(colorValue) {
            const selectedColor = this.colors.find((color) => color.value === colorValue);
            return selectedColor ? selectedColor.value : "";
        },
        filterOption(input, option) {
            return option.componentOptions.children[0].text.toLowerCase().indexOf(input.toLowerCase()) >= 0;
        },
        onTableChange() {
            // Tablo değiştiğinde seçili field'ı sıfırla
            this.cloneField.options.dropdown.remoteTableField = null;
        },
        onLinkedTableChange() {
            // Tablo değiştiğinde seçili labelField'ı sıfırla
            this.cloneField.options.linkedRecord.labelField = null;
        },
        onFieldsProcessingChange() {
            // Eğer "all" seçilirse, fieldsToProcess'i boşalt
            if (this.cloneField.options.ai.whichFieldsToProcess === "all") {
                this.cloneField.options.ai.fieldsToProcess = [];
            }
        },
        onLinkedRecordFieldChange() {
            // Tablo değiştiğinde seçili targetField'ı sıfırla
            this.cloneField.options.lookup.targetField = null;
        },
    },
});
</script>

<style scoped lang="scss">
.field-creator {
    .option-item {
        display: flex;
        align-items: center;
        margin-bottom: 10px;
    }
}
.ant-form-item {
    margin-bottom: 5px;
}
.color-box {
    width: 16px;
    height: 16px;
    border: 1px solid #000;
    display: inline-block;
    vertical-align: middle;
    margin-right: 5px;
}

.color-option {
    display: flex;
    align-items: center;
}
</style>
